import { HttpClient } from '@angular/common/http';
import {
  Inject,
  Injectable,
  InjectionToken,
  Optional,
  Signal,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment, MyHttpClientFactory } from '@ups/xplat/core';

export const EXPORT_ACCESS_URL = '/api/export-access';

/**
 * Base URL of the API whose `api/export-access` this app asks. Unset means Sparta. Sparta validates only Auth0
 * tokens, so an app that signs in with Entra would get a 401 there and hide every export even while the restriction
 * is off; such an app provides the API it already signs in to (PR 4033 thread 85477): web-hr-packet provides
 * `environment.urls.packetAPI`, web-software-security `environment.security.apiUrl`. Every API built on
 * AddCommonStartup applies the same rule from the same config.
 */
export const EXPORT_ACCESS_API_URL = new InjectionToken<string>(
  'EXPORT_ACCESS_API_URL'
);

interface ExportAccessResponse {
  exportAllowed?: boolean;
  ExportAllowed?: boolean;
}

/**
 * Whether the current user may export grid data to Excel/CSV (#34476).
 * The rule and its config live on the APIs (Sparta by default, see
 * {@link EXPORT_ACCESS_API_URL}), which also reject restricted export requests;
 * this only drives whether export controls are shown.
 *
 * Fails closed: `exportAllowed` is false until the server answers true, and stays
 * false on error, because some exports are generated in the browser and have no
 * server request to reject.
 */
@Injectable({
  providedIn: 'root',
})
export class ExportAccessService {
  private readonly http: HttpClient;
  private readonly allowed = signal(false);

  readonly exportAllowed: Signal<boolean> = this.allowed.asReadonly();

  /** Resolves with the server's answer (false on error), for code that configures export once, such as a Power BI embed. */
  readonly resolved: Promise<boolean>;

  constructor(
    httpFactory: MyHttpClientFactory,
    @Optional() @Inject(EXPORT_ACCESS_API_URL) apiUrl?: string | null
  ) {
    // No configured URL means the probe fails, which hides export (fail closed).
    this.http = httpFactory.createHttpClient(
      apiUrl || (environment.urls?.spartaAPI ?? '')
    );
    this.resolved = this.load();
  }

  private async load(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.get<ExportAccessResponse>(EXPORT_ACCESS_URL)
      );
      this.allowed.set(
        (response?.exportAllowed ?? response?.ExportAllowed) === true
      );
    } catch (error) {
      console.warn(
        'Export access check failed; export controls stay hidden.',
        error
      );
      this.allowed.set(false);
    }
    return this.allowed();
  }
}
